import { observer } from "mobx-react";
import React from "react";
import styled, { createGlobalStyle } from "styled-components";
import { getAppType } from "../../getAppType";
import { AppType } from "../../constants/AppType";
import { PathType, path, wsRouteMatch } from "../../url/WSRouter";
import { device } from "../../constants/mediaQueries";
import { useCurrentPath } from "../../url/useCurrentPath";
import { CVP_TOP_BAR_RESERVED_HEIGHT } from "../../constants/globalConsts";
import { concatClassnames } from "../../utils/util";
import { SideMenu, sideMenuLgWidth, sideMenuXlWidth } from "../nav/SideMenu";
import { TopBar } from "../nav/TopBar";
import { useWhoAmI } from "../../user/useWhoAmI";
import { ConsentDeleteDialogs } from "../profile/Privacy/ConsentDeleteDialogs";
import { useSettings } from "../../settings/useSettings";

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout = observer((props: LayoutProps) => {
  const { children } = props;

  const currentPath = useCurrentPath();
  const { confirmedHasProfileInConcern } = useWhoAmI();

  const isQuestionnairePage = wsRouteMatch(path("questionnaire"), currentPath);

  const isAdminPage = wsRouteMatch(
    path("admin", PathType.EXACT_OR_SUBPATH),
    currentPath
  );

  const isAttachProfilePage = wsRouteMatch(path("attach-profile"), currentPath);

  const settings = useSettings();

  const showFrame =
    !isQuestionnairePage && !isAdminPage && !isAttachProfilePage;

  const content = (
    <>
      <LayoutGlobalStyles />
      <StyledLayout
        className={concatClassnames([
          "cvp-layout",
          getAppType() === AppType.cvWebtempNo && "cvp-layout-cvwebtempno",
        ])}
      >
        {children}
      </StyledLayout>
    </>
  );

  if (!showFrame) return content;

  return (
    <>
      <PageContainer
        className={concatClassnames([
          "cvp-page-container",
          `cvp-page-container-${getAppType()}`,
          settings.showTopBar && "cvp-page-container-with-top-bar",
        ])}
      >
        {!!settings.showTopBar && <TopBar />}
        {!!confirmedHasProfileInConcern && !!settings.showSidebar && (
          <SideMenu />
        )}
        {!!confirmedHasProfileInConcern && <ConsentDeleteDialogs />}

        {content}
      </PageContainer>
    </>
  );
});

const PageContainer = styled.div`
  &.cvp-page-container {
    display: flex;
    position: relative;

    &.cvp-page-container-cvwebtempno {
      min-height: calc(100 * var(--vh));
    }

    &.cvp-page-container-with-top-bar {
      // Need this for the layout to start below the top bar
      padding-top: ${CVP_TOP_BAR_RESERVED_HEIGHT};
    }
  }
`;

const StyledLayout = styled.div`
  &.cvp-layout {
    display: flex;
    position: relative;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;

    &.cvp-layout-cvwebtempno {
      max-width: 100%;
      min-height: calc(100 * var(--vh) - ${CVP_TOP_BAR_RESERVED_HEIGHT});
    }

    > .p-inline-message-error {
      margin-top: 2rem;
      align-self: center;
    }
  }
`;

const LayoutGlobalStyles = createGlobalStyle`
  .cvp-side-menu ~ .cvp-layout.cvp-layout-cvwebtempno {
    @media ${device.md} {
      max-width: calc(100% - ${sideMenuLgWidth}px);
    }

    @media ${device.xl} {
      max-width: calc(100% - ${sideMenuXlWidth}px);
    }
  }

`;
